import { IconBuilding } from '@tabler/icons-react';
import { DockPanel, useDock } from '@hana/shell';
import { Bars, Columns, Donut, Kpis, Notis, Sec, Tasks, type DockNoti, type DockTask } from './crmDock';
import type { Customer } from './types';
import { useT } from '@hana/i18n';
import './i18n';

// Bảng phải của Mint-Customers (khổ 1920 mở sẵn): Phân tích · Việc (4) · Thông báo (2). Số liệu tính từ danh sách khách; Việc / Thông báo là mẫu của board.
const TASKS: DockTask[] = [
  { code: 'SLD', text: 'Follow Tôm Sóc Trăng Seafood — hẹn gọi lại', due: 'Quá hạn 2 ngày', tone: 'err' },
  { code: 'SLD', text: 'Gặp Nội thất Scansia tại Bình Dương', due: 'Hôm nay 15:00', tone: 'warn' },
  { code: 'DUY', text: 'Duyệt chuyển 2 khách TUANNT → ANDG', due: 'Chờ [SALE-MGR] DAVID' },
  { code: 'MYH', text: 'Xác minh tài khoản myHANA · NDP', due: 'Hạn 23/Oct/2026 17:30' },
];
const NOTIS: DockNoti[] = [
  { text: 'NAMMY yêu cầu tài khoản myHANA', when: '10 phút trước', tone: 'blue', unread: true },
  { text: 'GREENBUD vượt hạn mức · giữ DO tự bật', when: '1 giờ trước', tone: 'red', unread: true },
  { text: 'ANDG thêm 2 tiềm năng từ dữ liệu HQ', when: 'Hôm qua 16:20', tone: 'gray' },
  { text: 'Báo giá Live tháng 11 đã sẵn cho 6 khách', when: 'Hôm qua 09:00', tone: 'gray' },
];
const IDLE: Array<[string, number]> = [['EMDEES', 45], ['COCOGROUP', 38], ['ARRIVE', 31]];
type K = 'an' | 'task' | 'noti';

export function useCustomersDock() {
  return useDock<K>('an');
}

export function CustomersDock({ rows, dock }: { rows: Customer[]; dock: ReturnType<typeof useCustomersDock> }) {
  const t = useT();
  if (!dock.open) return null;
  const act = rows.filter((r) => r.jobs > 0).length;
  const kpi = [
    { k: t('crm.cdk.new'), v: '3' },
    { k: t('crm.cdk.overdue'), v: String(rows.filter((r) => r.credit.overdue > 0).length), tone: 'err' as const },
    { k: t('crm.cdk.myWait'), v: String(rows.filter((r) => r.my === 'wait').length), tone: 'warn' as const },
  ];
  const sale = ['DAVID', 'TUANNT', 'ANDG'].map((k) => ({ k, n: rows.filter((r) => r.sale === k).length }));
  const prov: Record<string, number> = {};
  rows.forEach((r) => {
    const p = r.province || '—';
    prov[p] = (prov[p] ?? 0) + 1;
  });
  const provRows = Object.entries(prov).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([k, n]) => ({ k: k === '—' ? t('crm.cdk.noProv') : k, n }));
  const cls = [1, 2, 3, 4, 5].map((n) => ({ k: t('crm.cdk.tier', { n }), n: rows.filter((r) => r.tier === n).length }));
  const slices = [
    { k: t('crm.cdk.active'), v: act, c: 'var(--d-blue)' },
    { k: t('crm.cdk.idleJob'), v: rows.length - act, c: 'var(--d-gray)' },
  ];
  const tabs = [
    { key: 'an' as const, label: t('crm.cdk.tabAn') },
    { key: 'task' as const, label: t('crm.cdk.tabTask'), n: TASKS.length },
    { key: 'noti' as const, label: t('crm.cdk.tabNoti'), n: 2 },
  ];
  return (
    <DockPanel tabs={tabs} tab={dock.tab} onTab={dock.show} onClose={dock.close}>
      {dock.tab === 'an' && (
        <>
          <Sec title={t('crm.at.25')}>
            <Donut slices={slices} total={rows.length} unit={t('crm.cdk.unit')} aria={`${rows.length} ${t('crm.cdk.unit')}: ${slices.map((d) => `${d.k} ${d.v}`).join(', ')}`} />
            <Kpis items={kpi} />
          </Sec>
          <Sec title={t('crm.at.26')} icon={<IconBuilding size={16} aria-hidden="true" />}>
            <Bars rows={sale} h={28} cols="70px 1fr 36px" label={(r) => `${r.k}: ${r.n} ${t('crm.cdk.unit')}`} />
          </Sec>
          <Sec title={t('crm.at.27')}>
            <Bars rows={provRows} h={26} cols="120px 1fr 30px" label={(r) => `${r.k}: ${r.n} ${t('crm.cdk.unit')}`} />
          </Sec>
          <Sec title={t('crm.at.28')}>
            <Columns rows={cls} aria={`${t('crm.cdk.byTier')}: ${cls.map((c) => `${c.k} ${c.n}`).join(', ')}`} />
            <div style={{ marginTop: 8, fontSize: 12, color: 'var(--lb)' }}>
              {t('crm.cdk.missing', { ct: rows.filter((r) => r.contactCount === 0).length, pv: rows.filter((r) => !r.province).length, bk: rows.filter((r) => r.bankCount === 0).length })}
            </div>
          </Sec>
          <Sec title={t('crm.at.29')}>
            <Bars warn rows={IDLE.map(([k, n]) => ({ k, n, text: t('crm.cdk.days', { n }) }))} h={28} cols="96px minmax(0,1fr) 64px" label={(r) => `${r.k} ${t('crm.cdk.days', { n: r.n })}`} />
            <div style={{ marginTop: 6, fontSize: 12, color: 'var(--lb)' }}>{t('crm.dk.idleNote')}</div>
          </Sec>
        </>
      )}
      {dock.tab === 'task' && <Tasks items={TASKS} />}
      {dock.tab === 'noti' && <Notis items={NOTIS} />}
    </DockPanel>
  );
}
